import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";

/**
 * ThemedSelect — a drop-in replacement for a native <select> that matches the
 * bayou dark aesthetic and renders identically across every OS / browser.
 * Native <select> popups inherit system chrome (white on Windows light mode)
 * which broke readability for our customers. This component owns its own
 * popover so the trigger + list are always our colours.
 *
 * The popover is portalled to document.body with fixed positioning so it can
 * float freely above modals, sticky bars, and any ancestor with overflow:auto
 * that would otherwise clip it. It also auto-flips upward when the trigger is
 * near the bottom of the viewport, so options never get hidden below the fold.
 *
 * Props:
 *   value: string (controlled)
 *   onChange: (value: string) => void
 *   options: Array<{ value: string, label: string, disabled?: boolean }>
 *   placeholder?: string    default "Select…"
 *   className?: string      appended to the trigger button className
 *   disabled?: boolean
 *   "data-testid"?: string  applied to the trigger for testing
 *
 * Keyboard: Enter/Space opens, Escape closes, ArrowUp/Down move focus,
 * Enter selects. Click outside closes. Fully accessible.
 */
export const ThemedSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  className = "",
  disabled = false,
  "data-testid": testId,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [popStyle, setPopStyle] = useState(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  const selected = options.find((o) => o.value === value);

  // Compute popover position anchored to the trigger. Always opens DOWNWARD
  // so the popover never overlays the sibling controls above the trigger —
  // e.g. the Disney/Off-site toggle tabs in the TripSetup modal, which
  // customers need to remain in view when scanning the list. Height is
  // capped by remaining viewport so short screens still get a scrollable
  // popover rather than one that spills below the fold.
  const computePosition = () => {
    const trg = triggerRef.current;
    if (!trg) return;
    const r = trg.getBoundingClientRect();
    const gap = 6;
    const IDEAL = 280;
    const spaceBelow = Math.max(120, window.innerHeight - r.bottom - 12);
    const maxH = Math.min(IDEAL, spaceBelow);
    setPopStyle({
      position: "fixed",
      left: `${r.left}px`,
      width: `${r.width}px`,
      top: `${r.bottom + gap}px`,
      maxHeight: `${maxH}px`,
    });
  };

  useLayoutEffect(() => {
    if (open) computePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => computePosition();
    const handler = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", handler);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      // Focus the currently selected option, or the first one.
      const idx = options.findIndex((o) => o.value === value);
      setActiveIdx(idx >= 0 ? idx : 0);
    }
  }, [open, value, options]);

  useEffect(() => {
    if (open && listRef.current && activeIdx >= 0) {
      const el = listRef.current.querySelector(`[data-index="${activeIdx}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx, open]);

  const commit = (val) => {
    onChange?.(val);
    setOpen(false);
  };

  const onKey = (e) => {
    if (disabled) return;
    if (!open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(options.length - 1, i + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const opt = options[activeIdx];
      if (opt && !opt.disabled) commit(opt.value);
    }
  };

  return (
    <div ref={wrapRef} className={`themed-select ${className}`} data-open={open || undefined}>
      <button
        ref={triggerRef}
        type="button"
        className="input themed-select__trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKey}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testId}
      >
        <span className={`themed-select__label ${selected ? "" : "themed-select__label--placeholder"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-[#FFD166] themed-select__chev" aria-hidden />
      </button>
      {open && popStyle && createPortal(
        <div className="themed-select__pop" role="listbox" ref={listRef} style={popStyle}>
          {options.map((opt, i) => {
            const isActive = i === activeIdx;
            const isSelected = opt.value === value;
            return (
              <div
                key={`${opt.value}-${i}`}
                data-index={i}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled || undefined}
                className={`themed-select__opt ${isActive ? "themed-select__opt--active" : ""} ${isSelected ? "themed-select__opt--selected" : ""} ${opt.disabled ? "themed-select__opt--disabled" : ""}`}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => !opt.disabled && commit(opt.value)}
                data-testid={testId ? `${testId}-opt-${opt.value || "empty"}` : undefined}
              >
                <span className="themed-select__opt-label">{opt.label}</span>
                {isSelected && <Check className="w-4 h-4 text-[#FFD166]" aria-hidden />}
              </div>
            );
          })}
          {options.length === 0 && (
            <div className="themed-select__opt themed-select__opt--disabled">No options</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ThemedSelect;
